import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Stepper } from "@/components/ui/stepper";
import { ChevronLeft, ChevronRight, Copy, CheckCircle2 } from "lucide-react";
import { useI18n } from '@/hooks/useI18n';
import { ShareLinkStep2Configure } from "./ShareLinkStep2Configure";
import { ShareLinkStep3Generate } from "./ShareLinkStep3Generate";
import { DashboardItinerary } from "@/lib/models/dashboard";
import { useCreateSharedLink } from "@/lib/shared-proposal/hooks/useCreateSharedLink";
import { toast } from "@/hooks/useToast";

interface ShareLinkFlowProps {
  initialItinerary?: DashboardItinerary;
  onBack: () => void;
  onComplete: (data: { shareUrl: string; linkPassword?: string; itinerary: DashboardItinerary }) => void;
}

export const ShareLinkFlow = ({
  initialItinerary,
  onBack,
  onComplete,
}: ShareLinkFlowProps) => {
  const { t } = useI18n();
  const [currentStep, setCurrentStep] = useState(1);
  const [linkPassword, setLinkPassword] = useState('');
  const [linkExpiry, setLinkExpiry] = useState<string | null>("30");

  const { generateLink, isGenerating, sharedLink } = useCreateSharedLink();

  const steps = [
    { label: t('itinerary.configureLink') },
    { label: t('itinerary.generateLink') },
  ];

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      onBack();
    }
  };

  const handleGenerateLink = async () => {
    if (!initialItinerary) return;

    try {
      // Convert days to ISO date
      let expiresAt: string | undefined = undefined;
      if (linkExpiry) {
        const days = parseInt(linkExpiry, 10);
        const date = new Date();
        date.setDate(date.getDate() + days);
        expiresAt = date.toISOString();
      }

      const result = await generateLink(
        initialItinerary.id,
        linkPassword,
        expiresAt
      );

      if (result) {
        toast({
          title: t("itinerary.linkGenerated"),
          description: t("itinerary.linkGeneratedDescription"),
        });
        onComplete({
          shareUrl: result.shareUrl,
          linkPassword: linkPassword || undefined,
          itinerary: initialItinerary,
        });
      } else {
        toast({
          title: t("common.error"),
          description: t("itinerary.linkGenerationError"),
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error generating link:', error);
      toast({
        title: t("common.error"),
        description: t("itinerary.linkGenerationError"),
        variant: "destructive",
      });
    }
  };

  const canProceedFromStep1 = linkPassword.trim().length > 0; // Password is required

  return (
    <div className="space-y-6">
      <Stepper steps={steps} currentStep={currentStep} />

      <div className="min-h-[400px]">
        {currentStep === 1 && (
          <ShareLinkStep2Configure
            linkPassword={linkPassword}
            setLinkPassword={setLinkPassword}
            linkExpiry={linkExpiry}
            setLinkExpiry={setLinkExpiry}
          />
        )}

        {currentStep === 2 && initialItinerary && (
          <ShareLinkStep3Generate
            itinerary={initialItinerary}
            linkPassword={linkPassword}
            linkExpiry={linkExpiry}
            onGenerate={handleGenerateLink}
            isGenerating={isGenerating}
            sharedLink={sharedLink}
          />
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6 border-t">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={isGenerating}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          {currentStep === 1 ? t('common.back') : t('common.previous')}
        </Button>

        {currentStep < 2 && (
          <Button
            onClick={handleNext}
            disabled={currentStep === 1 && !canProceedFromStep1}
          >
            {t('common.next')}
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
};
